import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, Download, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  generateGoogleCalendarUrl,
  generateOutlookCalendarUrl,
  generateYahooCalendarUrl,
  downloadICSFile,
} from "@/lib/calendarUtils";

const CALENDAR_OPTIONS = [
  {
    id: "google",
    label: "Google Calendar",
    icon: "📅",
    getUrl: generateGoogleCalendarUrl,
  },
  {
    id: "outlook",
    label: "Outlook",
    icon: "📧",
    getUrl: generateOutlookCalendarUrl,
  },
  {
    id: "yahoo",
    label: "Yahoo Calendar",
    icon: "📆",
    getUrl: generateYahooCalendarUrl,
  },
  {
    id: "ics",
    label: "Download .ics",
    icon: "⬇️",
    action: downloadICSFile,
  },
];

export default function AddToCalendarButton({ event, variant = "default", className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionClick = (option) => {
    if (option.action) {
      option.action(event);
    } else if (option.getUrl) {
      window.open(option.getUrl(event), "_blank", "noopener,noreferrer");
    }
    setIsOpen(false);
  };

  const baseStyles = {
    default: "px-3 py-1.5 rounded-lg text-xs sm:text-sm",
    compact: "px-2 py-1 rounded text-[11px] sm:text-xs",
    icon: "p-1.5 rounded-lg",
  };

  const buttonStyle = baseStyles[variant] || baseStyles.default;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/80 transition-colors ${buttonStyle}`}
        data-testid="add-to-calendar-btn"
      >
        <Calendar className="w-3.5 h-3.5" />
        {variant !== "icon" && (
          <>
            <span>Add to Calendar</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 bottom-full mb-1 w-48 rounded-lg py-1 z-[100] shadow-xl"
            style={{
              background: "rgba(20, 20, 20, 0.98)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(12px)",
            }}
            data-testid="calendar-dropdown"
          >
            {CALENDAR_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                data-testid={`calendar-option-${option.id}`}
              >
                <span className="text-base">{option.icon}</span>
                <span className="flex-1">{option.label}</span>
                {option.action ? (
                  <Download className="w-3.5 h-3.5 text-white/30" />
                ) : (
                  <ExternalLink className="w-3.5 h-3.5 text-white/30" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
